/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import { supabase, callClaude, BACKEND_URL } from "./lib/supabase";
import { S, STEPS } from "./styles/tokens";
import Spinner from "./components/Spinner";
import PlanBanner from "./components/PlanBanner";
import AuthView from "./views/AuthView";
import ResetPasswordView from "./views/ResetPasswordView";
import DashboardView from "./views/DashboardView";
import PricingView from "./views/PricingView";
import LandingView from "./views/LandingView";
import AnalyzeView from "./views/AnalyzeView";
import { startSmoothScroll, stopSmoothScroll, smoothScrollTo } from "./lib/motion";
import ErrorBoundary from "./components/ErrorBoundary";

function AppInner() {
  var [view, setView] = useState("loading"); // "loading" | "auth" | "landing" | "app" | "reset" | "dashboard" | "pricing"
  var [user, setUser] = useState(null);
  var [userPlan, setUserPlan] = useState("free");
  var [jd, setJd] = useState("");
  var [cv, setCv] = useState("");
  var [dzState, setDzState] = useState("idle");
  var [dzMsg, setDzMsg] = useState("");
  var [err, setErr] = useState("");
  var [loading, setLoading] = useState(false);
  var [loadStep, setLoadStep] = useState(0);
  var [prog, setProg] = useState(0);
  var [result, setResult] = useState(null);
  var [rewrite, setRewrite] = useState(null);
  var [cover, setCover] = useState(null);
  var [linkedin, setLinkedin] = useState(null);
  var [copied, setCopied] = useState({});
  var fileRef = useRef(null);
  var stepRef = useRef(null);

  // ── Smooth scroll (Lenis + GSAP ScrollTrigger), skipped under reduced-motion ──
  useEffect(function() {
    startSmoothScroll();
    return function() { stopSmoothScroll(); };
  }, []);

  // ── On mount: check Supabase session + handle Stripe return ──
  useEffect(function() {
    async function init() {
      // ✅ Check for password reset token in URL hash FIRST
      var hash = window.location.hash;
      if (hash && hash.includes("type=recovery")) {
        // Don't clear the hash yet — Supabase needs it to establish session
        setView("reset");
        return;
      }

      // Check for Stripe payment return
      var params = new URLSearchParams(window.location.search);
      var success = params.get("success");
      var sessionId = params.get("session_id");
      var plan = params.get("plan");
      var canceled = params.get("canceled");

      // Get current Supabase session
      var { data: { session } } = await supabase.auth.getSession();

      if (canceled) {
        window.history.replaceState({}, "", "/");
        if (session) {
          var { data: profile } = await supabase.from("users").select("plan").eq("id", session.user.id).single();
          setUser(session.user);
          setUserPlan((profile && profile.plan) || "free");
          setView("landing");
        } else {
          setView("landing");
        }
        return;
      }

      if (success && sessionId) {
        // Verify payment and save plan to Supabase
        try {
          var res = await fetch(BACKEND_URL + "/api/verify-session?session_id=" + sessionId);
          var data = await res.json();
          if (data.success) {
            var newPlan = data.plan || plan || "pro";
            if (session) {
              // Save plan to Supabase users table
              await supabase.from("users").upsert({
                id: session.user.id,
                email: session.user.email,
                plan: newPlan,
                plan_updated_at: new Date().toISOString(),
                stripe_customer_id: data.customerId || null,
              });
              setUser(session.user);
            }
            setUserPlan(newPlan);
            // Store as backup in localStorage too
            localStorage.setItem("syntrix_plan", newPlan);
            if (data.email) localStorage.setItem("syntrix_email", data.email);
            setView(session ? "app" : "auth"); // If no session, send to sign in so plan gets linked
          } else {
            if (session) { setUser(session.user); }
            setUserPlan("free");
            setView(session ? "app" : "landing");
          }
        } catch (e) {
          // Fallback: save from URL param
          if (plan) {
            localStorage.setItem("syntrix_plan", plan);
            setUserPlan(plan);
            if (session) {
              await supabase.from("users").upsert({ id: session.user.id, email: session.user.email, plan: plan });
              setUser(session.user);
            }
          }
          setView(session ? "app" : "auth");
        }
        window.history.replaceState({}, "", "/");
        return;
      }

      // Normal load — check existing session
      if (session) {
        var { data: prof } = await supabase.from("users").select("plan").eq("id", session.user.id).single();
        // Use Supabase plan, fall back to localStorage, then free
        var lsPlan = localStorage.getItem("syntrix_plan");
        var savedPlan = (prof && prof.plan && prof.plan !== "free") ? prof.plan : (lsPlan || "free");
        // If localStorage had a paid plan but DB says free, sync it to DB
        if (lsPlan && lsPlan !== "free" && (!prof || prof.plan === "free")) {
          await supabase.from("users").upsert({ id: session.user.id, email: session.user.email, plan: lsPlan });
          savedPlan = lsPlan;
        }
        setUser(session.user);
        setUserPlan(savedPlan);
        // Always go to dashboard on return visit
        setView("dashboard");
      } else {
        setView("landing");
      }
    }

    init();

    // Listen for auth changes
    var { data: { subscription } } = supabase.auth.onAuthStateChange(async function(event, session) {
      if (event === "SIGNED_OUT") {
        setUser(null);
        setUserPlan("free");
        setView("landing");
      }
      if (event === "PASSWORD_RECOVERY") {
        setView("reset");
      }
    });

    return function() { subscription.unsubscribe(); };
  }, []);

  function onLogin(authUser, plan) {
    setUser(authUser);
    setUserPlan(plan);
    // Always go to dashboard after login so user sees their plan
    setView("dashboard");
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setUser(null);
    setUserPlan("free");
    setResult(null);
    setView("landing");
  }

  function hasAccess(feature) {
    var features = {
      free:  ["ats_score"],
      pro:   ["ats_score","breakdown","keywords","suggestions","detailed_analysis","rewrite"],
      elite: ["ats_score","breakdown","keywords","suggestions","detailed_analysis","rewrite","cover_letter","linkedin"],
      team:  ["ats_score","breakdown","keywords","suggestions","detailed_analysis","rewrite","cover_letter","linkedin","bulk","api"],
    };
    return (features[userPlan] || features.free).indexOf(feature) !== -1;
  }

  function handleFile(file) {
    var name = file.name.toLowerCase();
    setDzState("loading");
    setDzMsg("Reading " + file.name + "…");

    if (name.endsWith(".txt") || file.type === "text/plain") {
      var r = new FileReader();
      r.onload = function(e) { setCv(e.target.result.trim()); setDzState("ok"); setDzMsg("✓ " + file.name + " imported"); };
      r.onerror = function() { setDzState("err"); setDzMsg("Could not read file. Paste your resume above."); };
      r.readAsText(file, "UTF-8");
    } else if (name.endsWith(".pdf") || file.type === "application/pdf") {
      var rPdf = new FileReader();
      rPdf.onload = function(e) {
        var arr = new Uint8Array(e.target.result);
        if (window.pdfjsLib) { runPDFExtract(arr, file.name); }
        else {
          var script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.onload = function() { window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"; runPDFExtract(arr, file.name); };
          script.onerror = function() { setDzState("err"); setDzMsg("PDF library failed. Please paste your resume above."); };
          document.head.appendChild(script);
        }
      };
      rPdf.onerror = function() { setDzState("err"); setDzMsg("Could not read PDF."); };
      rPdf.readAsArrayBuffer(file);
    } else if (name.endsWith(".docx")) {
      var rDocx = new FileReader();
      rDocx.onload = function(e) {
        var buf = e.target.result;
        if (window.mammoth) { runDOCXExtract(buf, file.name); }
        else {
          var script2 = document.createElement("script");
          script2.src = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
          script2.onload = function() { runDOCXExtract(buf, file.name); };
          script2.onerror = function() { setDzState("err"); setDzMsg("DOCX library failed. Please paste your resume above."); };
          document.head.appendChild(script2);
        }
      };
      rDocx.onerror = function() { setDzState("err"); setDzMsg("Could not read file."); };
      rDocx.readAsArrayBuffer(file);
    } else {
      setDzState("err");
      setDzMsg("Unsupported format. Use PDF, DOCX, or TXT — or paste your resume above.");
    }
  }

  function runPDFExtract(data, filename) {
    window.pdfjsLib.getDocument({ data: data }).promise.then(function(pdf) {
      var totalPages = pdf.numPages;
      var pages = new Array(totalPages);
      var completed = 0;
      function getOnePage(pageNum) {
        pdf.getPage(pageNum).then(function(pg) { return pg.getTextContent(); }).then(function(content) {
          pages[pageNum - 1] = content.items.map(function(i) { return i.str; }).join(" ");
          completed += 1;
          if (completed === totalPages) {
            var text = pages.join("\n\n").replace(/\s{3,}/g, " ").trim();
            if (text.length > 30) { setCv(text); setDzState("ok"); setDzMsg("✓ " + filename + " — " + text.length + " chars extracted"); }
            else { setDzState("err"); setDzMsg("Scanned/image PDF — no text layer. Please paste your resume above."); }
          }
        }).catch(function(e) { setDzState("err"); setDzMsg("PDF page error: " + e.message); });
      }
      for (var i = 1; i <= totalPages; i++) { getOnePage(i); }
    }).catch(function(e) { setDzState("err"); setDzMsg("PDF error: " + e.message); });
  }

  function runDOCXExtract(arrayBuffer, filename) {
    window.mammoth.extractRawText({ arrayBuffer: arrayBuffer }).then(function(res) {
      var text = res.value.trim();
      if (text.length > 30) { setCv(text); setDzState("ok"); setDzMsg("✓ " + filename + " imported"); }
      else { setDzState("err"); setDzMsg("Could not extract text from DOCX. Please paste above."); }
    }).catch(function(e) { setDzState("err"); setDzMsg("DOCX error: " + e.message); });
  }

  function onDrop(e) {
    e.preventDefault(); setDzState("idle");
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }

  async function runAnalysis() {
    setErr("");
    if (!jd && !cv) { setErr("Please paste a Job Description and your Resume."); return; }
    if (!jd) { setErr("Please paste the Job Description in the left box."); return; }
    if (!cv) { setErr("Please paste your Resume in the right box."); return; }
    if (jd.length < 40) { setErr("Job description too short — please paste the full JD."); return; }
    if (cv.length < 40) { setErr("Resume too short — please paste your full resume."); return; }

    // ── Free plan usage limit check ──
    if (userPlan === "free" && user) {
      var thisMonth = new Date().toISOString().slice(0, 7); // "2026-05"
      var { data: prof } = await supabase.from("users").select("analyses_count, analyses_month").eq("id", user.id).single();
      var count = (prof && prof.analyses_month === thisMonth) ? (prof.analyses_count || 0) : 0;
      if (count >= 5) {
        setErr("🔒 You've used all 5 free analyses this month. Upgrade to Pro for unlimited analyses!");
        return;
      }
      // Increment count
      await supabase.from("users").upsert({
        id: user.id,
        analyses_count: count + 1,
        analyses_month: thisMonth,
      });
    }

    setLoading(true); setLoadStep(0); setProg(2);
    setResult(null); setRewrite(null); setCover(null); setLinkedin(null);

    var step = 0;
    stepRef.current = setInterval(function() {
      if (step < STEPS.length) { setLoadStep(step); setProg(STEPS[step][1]); step++; }
    }, 1000);

    var prompt =
      "You are SYNTRIX, a world-class ATS expert. Analyse the Job Description and Resume below.\n\n" +
      "=== JOB DESCRIPTION ===\n" + jd + "\n\n=== RESUME ===\n" + cv + "\n\n" +
      "Respond with ONLY one valid JSON object. No markdown, no backticks, no text outside JSON.\n\n" +
      "{\"ats_score\":<0-100>,\"verdict\":\"<2-5 words>\",\"summary\":\"<2-3 sentences>\"," +
      "\"breakdown\":{\"keyword_match\":{\"score\":<0-25>,\"max\":25,\"note\":\"<one sentence>\"},\"skills_alignment\":{\"score\":<0-25>,\"max\":25,\"note\":\"<one sentence>\"},\"experience_relevance\":{\"score\":<0-25>,\"max\":25,\"note\":\"<one sentence>\"},\"formatting_quality\":{\"score\":<0-25>,\"max\":25,\"note\":\"<one sentence>\"}}," +
      "\"matched_keywords\":[\"kw1\",\"kw2\",\"kw3\",\"kw4\",\"kw5\",\"kw6\"],\"missing_keywords\":[\"kw1\",\"kw2\",\"kw3\",\"kw4\",\"kw5\",\"kw6\"]," +
      "\"chips\":[{\"label\":\"<label>\",\"type\":\"good\"},{\"label\":\"<label>\",\"type\":\"warn\"},{\"label\":\"<label>\",\"type\":\"bad\"}]," +
      "\"suggestions\":[\"<tip1>\",\"<tip2>\",\"<tip3>\",\"<tip4>\",\"<tip5>\"]," +
      "\"detailed_analysis\":\"<400-600 words with ### section headers: ### Keyword Analysis, ### Skills Gap, ### Experience Alignment, ### Formatting & Readability, ### Priority Recommendations>\"}";

    try {
      var raw = await callClaude(prompt, 3000);
      var clean = raw.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
      var s = clean.indexOf("{");
      var e2 = clean.lastIndexOf("}");
      if (s === -1 || e2 === -1) throw new Error("No JSON found in response.");
      var parsed = JSON.parse(clean.substring(s, e2 + 1));
      clearInterval(stepRef.current);
      setProg(100);
      await new Promise(function(resolve) { setTimeout(resolve, 400); });
      setLoading(false);
      setResult(parsed);
      if (hasAccess("rewrite")) setTimeout(function() { doGenerateRewrite(jd, cv); }, 300);
      if (hasAccess("cover_letter")) setTimeout(function() { doGenerateCover(jd, cv); }, 200);
      if (hasAccess("linkedin")) setTimeout(function() { doGenerateLinkedIn(jd, cv); }, 100);
    } catch (e) {
      clearInterval(stepRef.current);
      setLoading(false);
      setErr("Analysis failed: " + (e.message || "Unknown error"));
    }
  }

  async function doGenerateRewrite(jobDesc, resume) {
    try {
      var text = await callClaude("You are an expert resume writer. Rewrite this resume to score 90+ on ATS for this specific job.\n\nJOB DESCRIPTION:\n" + jobDesc + "\n\nORIGINAL RESUME:\n" + resume + "\n\nRules: Keep all real experience/dates/companies. Incorporate missing keywords naturally. Use strong action verbs and quantified achievements. Sections: PROFESSIONAL SUMMARY, EXPERIENCE, EDUCATION, SKILLS. Format for ATS readability. Use ### before each section heading. Start directly with the resume, no preamble.", 2500);
      setRewrite(text);
    } catch (e) { setRewrite("ERROR: " + e.message); }
  }

  async function doGenerateCover(jobDesc, resume) {
    try {
      var text = await callClaude("You are an expert cover letter writer. Write a compelling, personalised cover letter.\n\nJOB DESCRIPTION:\n" + jobDesc + "\n\nCANDIDATE RESUME:\n" + resume + "\n\nRules: Professional but warm tone. 3-4 paragraphs. Address as 'Dear Hiring Manager'. Use [Your Name] as placeholder. Start directly with 'Dear Hiring Manager,' no preamble.", 1500);
      setCover(text);
    } catch (e) { setCover("ERROR: " + e.message); }
  }

  async function doGenerateLinkedIn(jobDesc, resume) {
    try {
      var text = await callClaude("You are a LinkedIn optimisation expert.\n\nJOB DESCRIPTION:\n" + jobDesc + "\n\nCANDIDATE RESUME:\n" + resume + "\n\nProvide:\n### HEADLINE\nKeyword-rich headline under 220 characters.\n\n### ABOUT SECTION\nCompelling first-person about section under 2000 characters.\n\n### TOP 10 SKILLS\nList 10 recruiter-searchable skills.", 1500);
      setLinkedin(text);
    } catch (e) { setLinkedin("ERROR: " + e.message); }
  }

  function copyText(text, key) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(function() {
        setCopied(function(p) { return Object.assign({}, p, { [key]: true }); });
        setTimeout(function() { setCopied(function(p) { return Object.assign({}, p, { [key]: false }); }); }, 2000);
      });
    }
  }

  function doReset() {
    setResult(null); setJd(""); setCv(""); setErr(""); setDzMsg(""); setDzState("idle");
    setRewrite(null); setCover(null); setLinkedin(null);
  }

  var score = result ? Math.min(100, Math.max(0, parseInt(result.ats_score, 10) || 0)) : 0;

  // ── Pricing screen ──
  if (view === "pricing") {
    return (
      <PricingView
        user={user}
        plan={userPlan}
        onHome={function() { setView("landing"); }}
        onBackToDashboard={function() { setView("dashboard"); }}
        onNeedAuth={function() { setView("auth"); }}
        onSignOut={handleSignOut}
      />
    );
  }

  if (view === "loading") {
    return (
      <div style={{ ...S.authWrap, flexDirection: "column", gap: 16 }}>
        <div style={{ width: 48, height: 48, background: "#2c2420", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 22 22" fill="none" width={26} height={26}>
            <path d="M11 2L19 6.5V15.5L11 20L3 15.5V6.5L11 2Z" stroke="#f7f3ee" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M11 7V11L14 13" stroke="#c9963a" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx={11} cy={11} r={1.8} fill="#c9963a" />
          </svg>
        </div>
        <Spinner size={28} />
      </div>
    );
  }

  // ── Auth screen ──
  if (view === "auth") {
    return <AuthView onLogin={onLogin} onBack={function() { setView("landing"); }} />;
  }

  // ── Reset Password screen ──
  if (view === "reset") {
    return <ResetPasswordView onDone={function() { setView("auth"); }} />;
  }

  // ── Dashboard screen ──
  if (view === "dashboard") {
    return (
      <DashboardView
        user={user}
        plan={userPlan}
        onHome={function() { setView("landing"); }}
        onAnalyse={function() { setView("app"); }}
        onUpgrade={function() { setView("pricing"); }}
        onSignOut={handleSignOut}
      />
    );
  }

  return (
    <div style={S.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        textarea:focus { border-color: #d4c4ac !important; outline: none; }
        input:focus { border-color: #d4c4ac !important; outline: none; box-shadow: 0 0 0 3px rgba(201,150,58,.1); }
        body { background: #f7f3ee; }

        /* ── MOBILE RESPONSIVE ── */
        @media (max-width: 768px) {
          /* Header */
          header { padding: 0 16px !important; }
          .logo-tag { display: none; }

          /* Wrap */
          .wrap { padding: 0 16px !important; }

          /* Hero buttons */
          .hero-btns { flex-direction: column !important; align-items: center !important; }
          .hero-btns button { width: 100% !important; justify-content: center !important; }

          /* Stats bar */
          .stats-grid { grid-template-columns: repeat(2,1fr) !important; }
          .stats-grid > div { border-right: none !important; border-bottom: 1px solid rgba(247,243,238,.1) !important; }

          /* Score section */
          .score-sec { grid-template-columns: 1fr !important; text-align: center !important; }
          .score-sec > div:first-child { margin: 0 auto !important; }

          /* Keyword grid */
          .kw-grid { grid-template-columns: 1fr !important; }

          /* Inputs flex */
          .inp-flex { flex-direction: column !important; }

          /* Plan cards */
          .plan-grid { grid-template-columns: 1fr !important; }

          /* Auth card */
          .auth-card { padding: 28px 20px !important; }

          /* Dashboard grid */
          .dash-grid { grid-template-columns: 1fr !important; }

          /* Header buttons - hide some on mobile */
          .hide-mobile { display: none !important; }

          /* Premium headers */
          .prem-hdr { flex-direction: column !important; gap: 10px !important; align-items: flex-start !important; }

          /* Banner */
          .banner { padding: 9px 16px !important; font-size: 11px !important; }

          /* Section padding */
          .sec-pad { padding-top: 48px !important; padding-bottom: 48px !important; }
        }

        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr !important; }
          .hdr-logo-name { font-size: 16px !important; letter-spacing: 2px !important; }
        }
      `}</style>

      {/* HEADER */}
      <header style={{ ...S.header, padding: "0 20px" }}>
        <div style={S.logoRow} onClick={function() { setView("landing"); }}>
          <div style={S.logoMark}>
            <svg viewBox="0 0 22 22" fill="none" width={17} height={17}>
              <path d="M11 2L19 6.5V15.5L11 20L3 15.5V6.5L11 2Z" stroke="#f7f3ee" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M11 7V11L14 13" stroke="#c9963a" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx={11} cy={11} r={1.8} fill="#c9963a" />
            </svg>
          </div>
          <div>
            <div className="hdr-logo-name" style={S.logoName}>SYNTRIX</div>
            <div className="logo-tag" style={S.logoTag}>AI RESUME INTELLIGENCE</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button className="hide-mobile" style={{ ...S.hBtn, color: "#7a6e64", background: "transparent" }} onClick={function() { setView("landing"); }}>Home</button>
          <button className="hide-mobile" style={{ ...S.hBtn, color: "#7a6e64", background: "transparent" }} onClick={function() { document.getElementById("pricing") ? smoothScrollTo("#pricing") : setView("landing"); }}>Pricing</button>
          {user ? (
            <>
              <button className="hide-mobile" style={{ ...S.hBtn, color: "#7a6e64", background: "transparent" }} onClick={function() { setView("dashboard"); }}>Dashboard</button>
              <button style={{ ...S.hBtn, background: "#2c2420", color: "#f7f3ee", fontWeight: 600, fontSize: 11, padding: "8px 14px" }} onClick={function() { setView("app"); }}>Analyse →</button>
            </>
          ) : (
            <>
              <button className="hide-mobile" style={{ ...S.hBtn, color: "#7a6e64", background: "transparent" }} onClick={function() { setView("auth"); }}>Sign In</button>
              <button style={{ ...S.hBtn, background: "#2c2420", color: "#f7f3ee", fontWeight: 600, fontSize: 11, padding: "8px 14px" }} onClick={function() { setView("auth"); }}>Get Started →</button>
            </>
          )}
        </div>
      </header>

      {/* PLAN BANNER — show when logged in */}
      {user && <PlanBanner plan={userPlan} user={user} onSignOut={handleSignOut} />}

      {/* LANDING */}
      {view === "landing" && (
        <LandingView user={user} userPlan={userPlan} onNavigate={setView} />
      )}

      {/* APP */}
      {view === "app" && (
        <AnalyzeView
          user={user}
          userPlan={userPlan}
          jd={jd} setJd={setJd}
          cv={cv} setCv={setCv}
          dzState={dzState} dzMsg={dzMsg}
          fileRef={fileRef}
          onDzClick={function() { if (fileRef.current) fileRef.current.click(); }}
          onDragOver={function(e) { e.preventDefault(); setDzState("over"); }}
          onDragLeave={function() { setDzState("idle"); }}
          onDrop={onDrop}
          onFileChange={function(e) { if (e.target.files && e.target.files[0]) { handleFile(e.target.files[0]); e.target.value = ""; } }}
          err={err}
          loading={loading} loadStep={loadStep} prog={prog}
          runAnalysis={runAnalysis}
          result={result} score={score}
          rewrite={rewrite} cover={cover} linkedin={linkedin}
          copied={copied} copyText={copyText}
          hasAccess={hasAccess}
          doReset={doReset}
          onGoAuth={function() { setView("auth"); }}
          onGoLanding={function() { setView("landing"); }}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}
